import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { PdvFlavorModel } from 'src/app/models/Pdv/PdvFlavor.model';
import { CreatePdvFlavorModel } from 'src/app/models/Pdv/CreatePdvFlavor.model';
import { EditPdvFlavorModel } from 'src/app/models/Pdv/EditPdvFlavor.model';
import { AdministrativeService } from 'src/app/services/administrative/administrative.service';
import { AlertMessageService } from 'src/app/services/alert-message/alert-message.service';
import { TranslateService } from '@ngx-translate/core';
import { LoadingIndicatorComponent } from 'src/app/components/loading-indicator/loading-indicator.component';

@Component({
  selector: 'app-flavors-management',
  templateUrl: './flavors-management.component.html',
  styleUrls: ['./flavors-management.component.scss']
})
export class FlavorsManagementComponent implements OnInit {

  displayedColumns = [
    'name',
    'flavorName',
    'updateInstructions',
    'requiresFile',
    'requiresVersionCode',
    'fileAccept',
    'active',
    'createdAt',
    'actions'
  ];

  showForm: boolean = false;
  flavors: PdvFlavorModel[] = [];
  flavorForm!: FormGroup;
  editingFlavorId: number = 0;
  editingRowId: number | null = null;
  editingRowForm: { [key: number]: FormGroup } = {};

  constructor(
    private fb: FormBuilder,
    private adminService: AdministrativeService,
    private alertMessage: AlertMessageService,
    private dialog: MatDialog,
    private translateService: TranslateService
  ) { }

  ngOnInit(): void {
    this.initForm();
    this.loadFlavors();
  }

  public async refresh(): Promise<void> {
    await this.loadFlavors();
    this.resetForm()
  }

  private initForm(): void {
    this.flavorForm = this.fb.group({
      name: ['', Validators.required],
      flavorName: ['', Validators.required],
      updateInstructions: [''],
      requiresFile: [false],
      fileAccept: [{ value: '', disabled: true }],
      requiresVersionCode: [false]
    });

    this.flavorForm.get('requiresFile')?.valueChanges.subscribe(requiresFile => {
      const fileAccept = this.flavorForm.get('fileAccept');
      if (!fileAccept) return;

      if (requiresFile) {
        fileAccept.enable({ emitEvent: false });
        fileAccept.setValidators([Validators.required]);
      } else {
        fileAccept.disable({ emitEvent: false });
        fileAccept.clearValidators();
        fileAccept.reset();
      }

      fileAccept.updateValueAndValidity({ emitEvent: false });
    });
  }

  async saveFlavor(): Promise<void> {
    if (this.flavorForm.invalid) {
      this.alertWarning(this.t('admin.flavor-management.messages.errors.invalidForm'));
      return;
    }
    const payload = this.mapToCreateFlavorModel(this.flavorForm.value);
    const serviceMethod = this.adminService.createFlavor.bind(this.adminService);
    await this.saveFlavorRequest(
      payload,
      serviceMethod,
      this.t('admin.flavor-management.messages.status.successCreate'),
      this.t('admin.flavor-management.messages.status.failedCreate')
    );
  }

  startEditing(f: PdvFlavorModel): void {
    if (this.editingRowId !== null) return;

    this.editingRowId = f.id;

    const form = this.editingRowForm[f.id] = this.fb.group({
      name: [f.name, Validators.required],
      flavorName: [f.flavorName, Validators.required],
      updateInstructions: [f.updateInstructions],
      requiresFile: [f.requiresFile],
      fileAccept: [
        { value: f.fileAccept, disabled: !f.requiresFile }
      ],
      requiresVersionCode: [f.requiresVersionCode]
    });

    form.get('requiresFile')?.valueChanges.subscribe((value: boolean | null) => {
      const fileAccept = form.get('fileAccept');
      if (!fileAccept) return;

      const requiresFile = value === true;

      if (requiresFile) {
        fileAccept.enable({ emitEvent: false });
        fileAccept.setValidators([Validators.required]);
      } else {
        fileAccept.disable({ emitEvent: false });
        fileAccept.clearValidators();
        fileAccept.reset();
      }

      fileAccept.updateValueAndValidity({ emitEvent: false });
    });
  }

  saveRow(f: PdvFlavorModel): void {
    const form = this.editingRowForm[f.id];
    if (!form.valid) {
      this.alertWarning(this.t('admin.flavor-management.messages.errors.invalidForm'));
      return;
    }

    const payload: EditPdvFlavorModel = {
      id: f.id,
      name: form.value.name,
      flavorName: form.value.flavorName,
      updateInstructions: form.value.updateInstructions,
      requiresFile: form.value.requiresFile,
      requiresVersionCode: form.value.requiresVersionCode,
      fileAccept: form.value.requiresFile ? form.value.fileAccept : '',
      active: f.active
    };

    this.adminService.editFlavor(payload).then(success => {
      if (success) {
        f.name = form.value.name;
        f.flavorName = form.value.flavorName;
        f.updateInstructions = form.value.updateInstructions;
        f.requiresFile = form.value.requiresFile;
        f.requiresVersionCode = form.value.requiresVersionCode;
        f.fileAccept = form.value.requiresFile ? form.value.fileAccept : '';
        delete this.editingRowForm[f.id];
        this.editingRowId = null;
      } else this.alertWarning(this.t('admin.flavor-management.messages.errors.updateFailed'));
    });
  }

  cancelRowEdit(f: PdvFlavorModel): void {
    delete this.editingRowForm[f.id];
    this.editingRowId = null;
  }

  resetForm(): void {
    this.editingFlavorId = 0;
    this.showForm = false;
    this.flavorForm.reset();
  }

  async toggleActive(flavor: PdvFlavorModel): Promise<void> {
    try {
      const success = await this.adminService.ToggleActive(flavor.id);
      if (success) flavor.active = !flavor.active;
      else this.alertWarning(this.t('admin.flavor-management.messages.errors.failedToggle'));
    } catch (error) {
      console.error(error);
      this.alertWarning(this.t('admin.flavor-management.messages.errors.unexpectedError'));
    }
  }

  private async loadFlavors(): Promise<void> {
    try {
      this.flavors = await this.adminService.getFlavors();
    } catch (error) {
      console.error(error);
      this.alertMessage.alertErrorMessage(this.t('admin.flavor-management.messages.errors.loadUpdatesError'));
      this.flavors = [];
    }
  }

  private async saveFlavorRequest(
    payload: any,
    serviceMethod: (payload: any) => Promise<boolean>,
    successMessage: string,
    failureMessage: string
  ): Promise<void> {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.disableClose = true;
    dialogConfig.hasBackdrop = true;
    dialogConfig.data = { message: this.t('admin.flavor-management.messages.status.creating') };

    const loadingDialog = this.dialog.open(LoadingIndicatorComponent, dialogConfig);

    try {
      const ok = await serviceMethod(payload);
      loadingDialog.componentInstance.succeeded = ok;
      loadingDialog.componentInstance.failed = !ok;
      loadingDialog.componentInstance.message = ok ? successMessage : failureMessage;

      if (ok) {
        await this.loadFlavors();
        this.resetForm();
      }
    } catch {
      loadingDialog.componentInstance.failed = true;
      this.translateService.get('admin.flavor-management.messages.errors.failedCreate')
        .subscribe(translated => {
          loadingDialog.componentInstance.message = translated;
        });
    } finally {
      await this.delay(1000);
      loadingDialog.close();
    }
  }

  private mapToCreateFlavorModel(formValue: any): CreatePdvFlavorModel {
    return {
      name: formValue.name,
      flavorName: formValue.flavorName,
      updateInstructions: formValue.updateInstructions,
      requiresFile: formValue.requiresFile ?? false,
      requiresVersionCode: formValue.requiresVersionCode ?? false,
      fileAccept: formValue.fileAccept
    };
  }

  private mapToEditFlavorModel(formValue: any): EditPdvFlavorModel {
    return {
      id: formValue.id,
      name: formValue.name,
      flavorName: formValue.flavorName,
      updateInstructions: formValue.updateInstructions,
      requiresFile: formValue.requiresFile ,
      requiresVersionCode: formValue.requiresVersionCode,
      fileAccept: formValue.fileAccept,
      active: formValue.active
    };
  }

  private t(key: string, params?: any): string {
    return this.translateService.instant(key, params);
  }

  private alertWarning(msg: string): void {
    this.alertMessage.alertWarningMessage(msg);
  }

  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }
}